import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faPlus, faPlay, faCheck, faTimes,
  faSpinner, faSync, faTrash, faEye, faGlobe,
  faExternalLinkAlt, faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';
import axios from '../../config/axiosConfig';

const ATTACK_TYPES = [
  'sqli', 'xss', 'path_traversal', 'command_injection', 'xxe', 'ssrf',
  'ldap_injection', 'brute_force', 'credential_stuffing', 'api_abuse'
];

const WAF_ACTIONS = ['block', 'detect', 'pass'];

const BlueTeamScenarioBuilder = ({ webTarget, isDarkMode }) => {
  const [scenarios, setScenarios] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: '',
    attackType: 'sqli',
    targetEndpoint: { method: 'GET', url: '' },
    expectedWafAction: 'block',
    expectedSiemAlert: { ruleName: '', severity: 'high' }
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (webTarget?._id) {
      loadScenarios();
      loadCampaigns();
    }
  }, [webTarget]);

  const loadScenarios = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/web-reports/blue-team/${webTarget._id}`);
      setScenarios(res.data || []);
    } catch (err) {
      console.error('Failed to load blue team scenarios:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const res = await axios.get(`/campaigns/by-web-target/${webTarget._id}`);
      setCampaigns(res.data || []);
    } catch (err) {
      console.error('Failed to load linked campaigns:', err);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await axios.post('/web-reports/blue-team', {
        project: webTarget.project,
        webTarget: webTarget._id,
        name: form.name || `Blue Team: ${form.attackType} on ${form.targetEndpoint.url}`,
        blueTeamConfig: {
          attackType: form.attackType,
          targetEndpoint: form.targetEndpoint,
          expectedWafAction: form.expectedWafAction,
          expectedSiemAlert: form.expectedSiemAlert
        }
      });
      setShowCreate(false);
      setForm({ name: '', attackType: 'sqli', targetEndpoint: { method: 'GET', url: '' }, expectedWafAction: 'block', expectedSiemAlert: { ruleName: '', severity: 'high' } });
      loadScenarios();
    } catch (err) {
      console.error('Failed to create scenario:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/web-reports/blue-team/${id}`);
      loadScenarios();
    } catch {}
  };

  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const text = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const inputClass = `text-xs rounded border px-2 py-1.5 w-full ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'}`;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`px-3 py-2 border-b ${border} flex items-center justify-between ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faShieldAlt} className="text-blue-400" />
          <span className={`text-xs font-medium ${text}`}>Blue Team Detection Scenarios</span>
          <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>({scenarios.length})</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { loadScenarios(); loadCampaigns(); }} className={`p-1 text-xs ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
            <FontAwesomeIcon icon={faSync} />
          </button>
          <button onClick={() => setShowCreate(!showCreate)}
            className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex items-center gap-1">
            <FontAwesomeIcon icon={faPlus} /> New Scenario
          </button>
        </div>
      </div>

      {/* Linked Campaigns Section */}
      {campaigns.length > 0 && (
        <div className={`px-3 py-2 border-b ${border} ${isDarkMode ? 'bg-gray-800/50' : 'bg-orange-50/50'}`}>
          <div className="flex items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faExchangeAlt} className="text-orange-400 text-xs" />
            <span className={`text-[10px] font-medium ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>Linked Campaigns ({campaigns.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {campaigns.map(c => (
              <div key={c._id} className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded border ${
                isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-white'
              }`}>
                <span className={`px-1 py-0.5 rounded text-[9px] ${
                  c.framework === 'owasp_wstg' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                }`}>{c.framework === 'owasp_wstg' ? 'WSTG' : 'MITRE'}</span>
                <span className={text}>{c.name}</span>
                <span className={`px-1 py-0.5 rounded text-[9px] ${
                  c.status === 'planned' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                  c.status === 'active' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                }`}>{c.status}</span>
                {c.framework === 'owasp_wstg' && c.wstgTests && (
                  <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>{c.wstgTests.length} tests</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className={`px-4 py-3 border-b ${border} space-y-2 ${isDarkMode ? 'bg-gray-800/50' : 'bg-blue-50/50'}`}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Scenario Name</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., SQLi detection on /api/users" className={inputClass} />
            </div>
            <div>
              <label className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Attack Type</label>
              <select value={form.attackType} onChange={e => setForm(p => ({ ...p, attackType: e.target.value }))} className={inputClass}>
                {ATTACK_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Target Endpoint</label>
              <div className="flex gap-1">
                <select value={form.targetEndpoint.method} onChange={e => setForm(p => ({ ...p, targetEndpoint: { ...p.targetEndpoint, method: e.target.value } }))} className={`${inputClass} w-20`}>
                  {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(m => <option key={m}>{m}</option>)}
                </select>
                <input value={form.targetEndpoint.url} onChange={e => setForm(p => ({ ...p, targetEndpoint: { ...p.targetEndpoint, url: e.target.value } }))} placeholder="/api/users" className={inputClass} />
              </div>
            </div>
            <div>
              <label className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Expected WAF Action</label>
              <select value={form.expectedWafAction} onChange={e => setForm(p => ({ ...p, expectedWafAction: e.target.value }))} className={inputClass}>
                {WAF_ACTIONS.map(a => <option key={a} value={a}>{a.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Expected SIEM Rule Name</label>
              <input value={form.expectedSiemAlert.ruleName} onChange={e => setForm(p => ({ ...p, expectedSiemAlert: { ...p.expectedSiemAlert, ruleName: e.target.value } }))} placeholder="e.g., SQL Injection Detected" className={inputClass} />
            </div>
            <div>
              <label className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Expected Alert Severity</label>
              <select value={form.expectedSiemAlert.severity} onChange={e => setForm(p => ({ ...p, expectedSiemAlert: { ...p.expectedSiemAlert, severity: e.target.value } }))} className={inputClass}>
                {['critical', 'high', 'medium', 'low'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowCreate(false)} className={`px-3 py-1.5 text-xs rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>Cancel</button>
            <button onClick={handleCreate} disabled={creating} className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50">
              {creating ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Create Scenario'}
            </button>
          </div>
        </div>
      )}

      {/* Scenarios list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className={`p-8 text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}><FontAwesomeIcon icon={faSpinner} spin /> Loading...</div>
        ) : scenarios.length === 0 ? (
          <div className={`p-8 text-center ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            <FontAwesomeIcon icon={faShieldAlt} className="text-3xl mb-3" />
            <div className="text-sm font-medium mb-1">No Blue Team Scenarios</div>
            <div className="text-xs">Create detection validation scenarios to test WAF/IDS rules.</div>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className={isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}>
                <th className="px-3 py-2 text-left font-medium">Scenario</th>
                <th className="px-3 py-2 text-left font-medium">Attack</th>
                <th className="px-3 py-2 text-left font-medium">Endpoint</th>
                <th className="px-3 py-2 text-left font-medium">Expected WAF</th>
                <th className="px-3 py-2 text-left font-medium">Expected SIEM</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map(s => (
                <tr key={s._id} className={`border-b ${border} ${isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}`}>
                  <td className={`px-3 py-2 font-medium ${text}`}>{s.name}</td>
                  <td className={`px-3 py-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                    {s.blueTeamConfig?.attackType?.replace(/_/g, ' ') || '-'}
                  </td>
                  <td className={`px-3 py-2 font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {s.blueTeamConfig?.targetEndpoint?.method} {s.blueTeamConfig?.targetEndpoint?.url || '-'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      s.blueTeamConfig?.expectedWafAction === 'block' ? 'bg-green-900/30 text-green-400' :
                      s.blueTeamConfig?.expectedWafAction === 'detect' ? 'bg-yellow-900/30 text-yellow-400' :
                      'bg-red-900/30 text-red-400'
                    }`}>
                      {s.blueTeamConfig?.expectedWafAction || '-'}
                    </span>
                  </td>
                  <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {s.blueTeamConfig?.expectedSiemAlert?.ruleName || '-'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      s.status === 'completed' ? 'bg-green-900/30 text-green-400' :
                      s.status === 'running' ? 'bg-blue-900/30 text-blue-400' :
                      'bg-gray-700 text-gray-400'
                    }`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => handleDelete(s._id)} className="text-red-400 hover:text-red-300">
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BlueTeamScenarioBuilder;
